import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import BlogCard from './BlogCard';
import SEO from '../SEO';

interface BlogPostData {
    _id: string;
    title: string;
    content: string;
    excerpt: string;
    featuredImage?: string;
    author: {
        name: string;
        avatar?: string;
        bio: string;
    };
    category: string;
    tags: string[];
    readTime: number;
    views: number;
    likes: number;
    publishedAt: string;
    slug: string;
    seoData?: {
        metaTitle?: string;
        metaDescription?: string;
        keywords?: string[];
    };
}

interface UserInteraction {
    isFavorited: boolean;
    isMemorized: boolean;
    readProgress: number;
    lastReadAt: string;
}

interface BlogPostResponse {
    post: BlogPostData;
    userInteraction: UserInteraction | null;
    relatedPosts: BlogPostData[];
}

export default function BlogPost() {
    const { slug } = useParams<{ slug: string }>();
    const { currentUser } = useAuth();
    const [data, setData] = useState<BlogPostResponse | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [readProgress, setReadProgress] = useState(0);
    const [aiSummary, setAiSummary] = useState<string | null>(null);
    const [loadingAI, setLoadingAI] = useState(false);
    const [showAISummary, setShowAISummary] = useState(false);

    // Fetch blog post data
    useEffect(() => {
        const fetchPost = async () => {
            if (!slug) return;

            try {
                setLoading(true);
                setError(null);

                const params = new URLSearchParams();
                if (currentUser?.uid) {
                    params.set('userId', currentUser.uid);
                }

                const response = await fetch(`/api/blog/posts/${slug}?${params}`);
                const result = await response.json();

                if (result.success) {
                    setData(result.data);
                    if (result.data.userInteraction) {
                        setReadProgress(result.data.userInteraction.readProgress);
                    }
                } else {
                    setError(result.error || 'Failed to load blog post');
                }
            } catch (err) {
                setError('Failed to load blog post');
                console.error('Error fetching post:', err);
            } finally {
                setLoading(false);
            }
        };

        fetchPost();
    }, [slug, currentUser]);

    // Track reading progress
    useEffect(() => {
        if (!data?.post._id || !currentUser) return;

        const handleScroll = () => {
            const article = document.querySelector('#blog-content');
            if (!article) return;

            const scrollTop = window.scrollY;
            const docHeight = article.scrollHeight;
            const winHeight = window.innerHeight;
            const scrollPercent = Math.min(Math.round((scrollTop / (docHeight - winHeight)) * 100), 100);

            if (scrollPercent > readProgress && scrollPercent % 10 === 0) {
                setReadProgress(scrollPercent);
                updateReadProgress(scrollPercent);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, [data, currentUser, readProgress]);

    const updateReadProgress = async (progress: number) => {
        if (!data?.post._id || !currentUser) return;

        try {
            await fetch(`/api/blog/progress/${data.post._id}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${await currentUser.getIdToken()}`
                },
                body: JSON.stringify({ progress })
            });
        } catch (error) {
            console.error('Error updating progress:', error);
        }
    };

    const toggleFavorite = async () => {
        if (!data?.post._id || !currentUser) return;

        try {
            const response = await fetch(`/api/blog/favorite/${data.post._id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await currentUser.getIdToken()}`
                }
            });

            const result = await response.json();
            if (result.success) {
                setData(prev => ({
                    ...prev!,
                    userInteraction: {
                        ...prev!.userInteraction!,
                        isFavorited: result.data.isFavorited
                    }
                }));
            }
        } catch (error) {
            console.error('Error toggling favorite:', error);
        }
    };

    const toggleMemorize = async () => {
        if (!data?.post._id || !currentUser) return;

        try {
            const response = await fetch(`/api/blog/memorize/${data.post._id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await currentUser.getIdToken()}`
                }
            });

            const result = await response.json();
            if (result.success) {
                setData(prev => ({
                    ...prev!,
                    userInteraction: {
                        ...prev!.userInteraction!,
                        isMemorized: result.data.isMemorized
                    }
                }));
            }
        } catch (error) {
            console.error('Error toggling memorize:', error);
        }
    };

    const generateAISummary = async () => {
        if (!data?.post._id || !currentUser) return;

        try {
            setLoadingAI(true);
            const response = await fetch(`/api/blog/ai-summary/${data.post._id}`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${await currentUser.getIdToken()}`
                }
            });

            const result = await response.json();
            if (result.success) {
                setAiSummary(result.data.summary);
                setShowAISummary(true);
            }
        } catch (error) {
            console.error('Error generating AI summary:', error);
        } finally {
            setLoadingAI(false);
        }
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getCategoryColor = (category: string) => {
        const colors = {
            'grammar': 'bg-blue-100 text-blue-800',
            'vocabulary': 'bg-green-100 text-green-800',
            'culture': 'bg-purple-100 text-purple-800',
            'tips': 'bg-yellow-100 text-yellow-800',
            'success-stories': 'bg-pink-100 text-pink-800',
            'ai-learning': 'bg-indigo-100 text-indigo-800'
        };
        return colors[category as keyof typeof colors] || 'bg-gray-100 text-gray-800';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-200 rounded mb-4 w-3/4"></div>
                        <div className="h-4 bg-gray-200 rounded mb-8 w-1/2"></div>
                        <div className="h-64 bg-gray-200 rounded mb-8"></div>
                        <div className="space-y-4">
                            {[...Array(8)].map((_, i) => (
                                <div key={i} className="h-4 bg-gray-200 rounded"></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="bg-white rounded-xl p-8 shadow-lg max-w-md mx-auto">
                        <svg className="w-16 h-16 text-red-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <h2 className="text-xl font-semibold text-gray-800 mb-2">Post Not Found</h2>
                        <p className="text-gray-600 mb-6">{error || 'The blog post you are looking for does not exist.'}</p>
                        <Link
                            to="/blog"
                            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Back to Blog
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const { post, userInteraction, relatedPosts } = data;

    return (
        <div className="min-h-screen bg-gray-50">
            {/* SEO */}
                  <SEO 
        page="blog-post"
        title={post.seoData?.metaTitle || post.title}
        description={post.seoData?.metaDescription || post.excerpt}
        keywords={post.seoData?.keywords || post.tags}
      />

            {/* Reading Progress Bar */}
            <div className="fixed top-0 left-0 w-full h-1 bg-gray-200 z-50">
                <div
                    className="h-full bg-gradient-to-r from-blue-500 to-purple-600 transition-all duration-300"
                    style={{ width: `${readProgress}%` }}
                ></div>
            </div>

            {/* Breadcrumb */}
            <div className="bg-white border-b border-gray-200">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <nav className="flex items-center space-x-2 text-sm text-gray-500">
                        <Link to="/" className="hover:text-blue-600">Home</Link>
                        <span>›</span>
                        <Link to="/blog" className="hover:text-blue-600">Blog</Link>
                        <span>›</span>
                        <span className="text-gray-800 font-medium">{post.title}</span>
                    </nav>
                </div>
            </div>

            <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                {/* Header */}
                <header className="mb-12">
                    {/* Category & Date */}
                    <div className="flex items-center space-x-4 mb-6">
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getCategoryColor(post.category)}`}>
                            {post.category.replace('-', ' ').toUpperCase()}
                        </span>
                        <span className="text-gray-500 text-sm">{formatDate(post.publishedAt)}</span>
                        <span className="text-gray-500 text-sm">{post.readTime} min read</span>
                    </div>

                    {/* Title */}
                    <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
                        {post.title}
                    </h1>

                    {/* Excerpt */}
                    <p className="text-xl text-gray-600 leading-relaxed mb-8">
                        {post.excerpt}
                    </p>

                    {/* Author & Stats */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4">
                            <div className="w-12 h-12 bg-gradient-to-br from-blue-400 to-purple-600 rounded-full flex items-center justify-center">
                                {post.author.avatar ? (
                                    <img
                                        src={post.author.avatar}
                                        alt={post.author.name}
                                        className="w-12 h-12 rounded-full object-cover"
                                    />
                                ) : (
                                    <span className="text-white font-semibold text-lg">
                                        {post.author.name.charAt(0)}
                                    </span>
                                )}
                            </div>
                            <div>
                                <h3 className="font-semibold text-gray-800">{post.author.name}</h3>
                                <p className="text-sm text-gray-600">{post.author.bio}</p>
                            </div>
                        </div>

                        <div className="flex items-center space-x-6 text-sm text-gray-500">
                            <div className="flex items-center space-x-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                </svg>
                                <span>{post.views} views</span>
                            </div>
                            <div className="flex items-center space-x-1">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                </svg>
                                <span>{post.likes} likes</span>
                            </div>
                        </div>
                    </div>
                </header>

                {/* Featured Image */}
                {post.featuredImage && (
                    <div className="mb-12">
                        <img
                            src={post.featuredImage}
                            alt={post.title}
                            className="w-full h-64 md:h-96 object-cover rounded-2xl shadow-lg"
                        />
                    </div>
                )}

                {/* User Actions (if logged in) */}
                {currentUser && (
                    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-4">
                                <button
                                    onClick={toggleFavorite}
                                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${userInteraction?.isFavorited
                                            ? 'bg-red-50 text-red-600 hover:bg-red-100'
                                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                        }`}
                                >
                                    <svg className="w-5 h-5" fill={userInteraction?.isFavorited ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                                    </svg>
                                    <span>{userInteraction?.isFavorited ? 'Favorited' : 'Add to Favorites'}</span>
                                </button>

                                <button
                                    onClick={toggleMemorize}
                                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg transition-colors ${userInteraction?.isMemorized
                                            ? 'bg-green-50 text-green-600 hover:bg-green-100'
                                            : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                                        }`}
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    <span>{userInteraction?.isMemorized ? 'Memorized' : 'Mark as Memorized'}</span>
                                </button>

                                <button
                                    onClick={generateAISummary}
                                    disabled={loadingAI}
                                    className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors disabled:opacity-50"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    <span>{loadingAI ? 'Generating...' : 'AI Summary'}</span>
                                </button>
                            </div>

                            {readProgress > 0 && (
                                <div className="text-sm text-gray-600">
                                    Reading progress: {readProgress}%
                                </div>
                            )}
                        </div>

                        {/* AI Summary */}
                        {showAISummary && aiSummary && (
                            <div className="mt-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                                <h4 className="font-semibold text-purple-800 mb-2 flex items-center">
                                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                    </svg>
                                    AI Summary
                                </h4>
                                <div className="text-purple-700 text-sm whitespace-pre-line">
                                    {aiSummary}
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Content */}
                <div
                    id="blog-content"
                    className="prose prose-lg max-w-none mb-12"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                />

                {/* Tags */}
                {post.tags.length > 0 && (
                    <div className="mb-12">
                        <h3 className="text-lg font-semibold text-gray-800 mb-4">Tags</h3>
                        <div className="flex flex-wrap gap-2">
                            {post.tags.map((tag, index) => (
                                <Link
                                    key={index}
                                    to={`/blog?tag=${tag}`}
                                    className="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 transition-colors"
                                >
                                    #{tag}
                                </Link>
                            ))}
                        </div>
                    </div>
                )}

                {/* Related Posts */}
                {relatedPosts.length > 0 && (
                    <div className="border-t border-gray-200 pt-12">
                        <h3 className="text-2xl font-bold text-gray-800 mb-8">Related Articles</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {relatedPosts.map((relatedPost) => (
                                <BlogCard key={relatedPost._id} post={relatedPost} />
                            ))}
                        </div>
                    </div>
                )}
            </article>
        </div>
    );
} 